/**
 * Rolling performance telemetry. Render and simulation timings are tracked
 * separately so a slow frame can be attributed to the right side.
 */

class Window {
  private readonly values: Float64Array;
  private index = 0;
  private count = 0;

  constructor(size: number) {
    this.values = new Float64Array(size);
  }

  push(v: number): void {
    this.values[this.index] = v;
    this.index = (this.index + 1) % this.values.length;
    if (this.count < this.values.length) this.count++;
  }

  avg(): number {
    if (this.count === 0) return 0;
    let s = 0;
    for (let i = 0; i < this.count; i++) s += this.values[i]!;
    return s / this.count;
  }

  max(): number {
    let m = 0;
    for (let i = 0; i < this.count; i++) m = Math.max(m, this.values[i]!);
    return m;
  }

  get size(): number {
    return this.count;
  }
}

export interface PerfSnapshot {
  fps: number;
  frameAvgMs: number;
  frameWorstMs: number;
  renderAvgMs: number;
  simStepAvgMs: number;
  simStepMaxMs: number;
  queryAvgMsPerTick: number;
  queriesPerTick: number;
  catchUpMax: number;
  stallCount: number;
  heapMB: number | null;
}

export class PerfMonitor {
  private readonly frames = new Window(240);
  private readonly renders = new Window(240);
  private readonly steps = new Window(480);
  private readonly queryMs = new Window(480);
  private readonly queryCount = new Window(480);
  private readonly catchUp = new Window(240);
  stallCount = 0;

  frame(frameMs: number, renderMs: number, ticksThisFrame: number): void {
    this.frames.push(frameMs);
    this.renders.push(renderMs);
    this.catchUp.push(ticksThisFrame);
  }

  tick(stepMs: number, queryMs: number, queries: number): void {
    this.steps.push(stepMs);
    this.queryMs.push(queryMs);
    this.queryCount.push(queries);
  }

  snapshot(): PerfSnapshot {
    const frameAvg = this.frames.avg();
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
    return {
      fps: frameAvg > 0 ? 1000 / frameAvg : 0,
      frameAvgMs: frameAvg,
      frameWorstMs: this.frames.max(),
      renderAvgMs: this.renders.avg(),
      simStepAvgMs: this.steps.avg(),
      simStepMaxMs: this.steps.max(),
      queryAvgMsPerTick: this.queryMs.avg(),
      queriesPerTick: this.queryCount.avg(),
      catchUpMax: this.catchUp.max(),
      stallCount: this.stallCount,
      heapMB: mem ? mem.usedJSHeapSize / (1024 * 1024) : null,
    };
  }
}
